import React, { useState } from 'react';
import { Companion, CompanionAsset } from '../services/companionService';
import OptimizedVideo from './ui/OptimizedVideo';

interface CompanionDetailModalProps {
  companion: Companion;
  isOpen: boolean;
  onClose: () => void;
  onStartChat: () => void;
}

const CompanionDetailModal: React.FC<CompanionDetailModalProps> = ({
  companion,
  isOpen,
  onClose,
  onStartChat
}) => {
  const [selectedAssetIndex, setSelectedAssetIndex] = useState(0);

  if (!isOpen) return null;

  // Handle both new and legacy assets structure
  let assets: any[] = [];
  let selectedAsset: any = null;

  if (companion.assets && typeof companion.assets === 'object' && !Array.isArray(companion.assets)) {
    // New assets structure
    const newAssets = companion.assets;
    const gallery = newAssets.gallery || [];
    
    // Process gallery assets
    const galleryAssets = gallery.map((asset: any) => ({
      photo_id: asset.asset_id,
      presigned_url: asset.presigned_url,
      asset_type: asset.type === 'photo' ? 'image' as const : 'video' as const,
      is_primary: false
    }));
    
    assets = [...galleryAssets];
    
    // Add primary assets if they exist
    if (newAssets.primary_photo) {
      assets.unshift({
        photo_id: 'primary_photo',
        presigned_url: newAssets.primary_photo.presigned_url,
        asset_type: 'image',
        is_primary: true
      });
    }
    if (newAssets.primary_video) {
      assets.unshift({
        photo_id: 'primary_video',
        presigned_url: newAssets.primary_video.presigned_url,
        asset_type: 'video',
        is_primary: true
      });
    }
  } else {
    // Legacy assets structure
    assets = companion.assets as CompanionAsset[] || [];
  }

  selectedAsset = assets[selectedAssetIndex];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-dark-600">
          <div>
            <h2 className="text-2xl font-bold text-white">
              {typeof companion.name === 'object' && companion.name
                ? (companion.name.nick_name || `${companion.name.first_name || ''} ${companion.name.last_name || ''}`.trim() || 'Unknown')
                : String(companion.name || 'Unknown')}
            </h2>
            {companion.age && (
              <p className="text-dark-300">Age: {companion.age}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-dark-700 hover:bg-dark-600 flex items-center justify-center text-white transition-colors"
          >
            ✕
          </button>
        </div>

        <div className="flex flex-col lg:flex-row h-full">
          {/* Media Section */}
          <div className="lg:w-2/3 p-6">
            {/* Main Media Display */}
            <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-dark-700 mb-4">
              {selectedAsset ? (
                selectedAsset.asset_type === "video" ? (
                  <OptimizedVideo
                    src={selectedAsset.presigned_url}
                    className="w-full h-full"
                    controls={true}
                    autoPlay={false}
                    muted={true}
                    loop={false}
                    priority={true}
                  />
                ) : (
                  <img
                    src={selectedAsset.presigned_url}
                    alt={`${companion.name} ${selectedAssetIndex + 1}`}
                    className="w-full h-full object-cover"
                  />
                )
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-accent-purple to-accent-pink flex items-center justify-center">
                  <span className="text-6xl">🤖</span>
                </div>
              )}
            </div>

            {/* Media Thumbnails */}
            {assets.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {assets.map((asset, index) => (
                  <button
                    key={asset.photo_id}
                    onClick={() => setSelectedAssetIndex(index)}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedAssetIndex === index
                        ? 'border-accent-purple'
                        : 'border-dark-600 hover:border-dark-500'
                    }`}
                  >
                    {asset.asset_type === "video" ? (
                      <div className="w-full h-full bg-dark-600 flex items-center justify-center">
                        <span className="text-white text-xs">▶️</span>
                      </div>
                    ) : (
                      <img
                        src={asset.presigned_url}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info Section */}
          <div className="lg:w-1/3 p-6 overflow-y-auto">
            <div className="space-y-6">
              {/* Description */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">About</h3>
                <p className="text-dark-300 leading-relaxed">
                  {companion.description || 'No description available.'}
                </p>
              </div>

              {/* Personality */}
              {companion.personality && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Personality</h3>
                  <p className="text-dark-300 leading-relaxed">
                    {typeof companion.personality === 'string' 
                      ? companion.personality 
                      : JSON.stringify(companion.personality, null, 2)
                    }
                  </p>
                </div>
              )}

              {/* Attributes */}
              {companion.attributes && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Attributes</h3>
                  <div className="space-y-3">
                    {companion.attributes.physical && (
                      <div>
                        <h4 className="text-sm font-medium text-white mb-1">Physical</h4>
                        <div className="text-sm text-dark-300 space-y-1">
                          {companion.attributes.physical.ethnicity && (
                            <div>Ethnicity: {companion.attributes.physical.ethnicity}</div>
                          )}
                          {companion.attributes.physical.eye_color && (
                            <div>Eye Color: {companion.attributes.physical.eye_color}</div>
                          )}
                          {companion.attributes.physical.hair_color && (
                            <div>Hair Color: {companion.attributes.physical.hair_color}</div>
                          )}
                          {companion.attributes.physical.hair_style && (
                            <div>Hair Style: {companion.attributes.physical.hair_style}</div>
                          )}
                          {companion.attributes.physical.body_type && (
                            <div>Body Type: {companion.attributes.physical.body_type}</div>
                          )}
                        </div>
                      </div>
                    )}
                    {companion.attributes.personal && (
                      <div>
                        <h4 className="text-sm font-medium text-white mb-1">Personal</h4>
                        <div className="text-sm text-dark-300 space-y-1">
                          {companion.attributes.personal.occupation && (
                            <div>Occupation: {companion.attributes.personal.occupation}</div>
                          )}
                          {companion.attributes.personal.hobby && (
                            <div>Hobby: {companion.attributes.personal.hobby}</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Categories */}
              {companion.categories && companion.categories.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Categories</h3>
                  <div className="flex flex-wrap gap-2">
                    {companion.categories.map((category, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-accent-purple/20 text-accent-purple text-sm rounded-full"
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1 text-red-400">
                  <span>👍</span>
                  <span className="text-white">{(companion as any).likes_count || 0}</span>
                </div>
                <div className="flex items-center gap-1 text-blue-400">
                  <span>💬</span>
                  <span className="text-white">{(companion as any).messages_count || 0}</span>
                </div>
              </div>

              {/* Start Chat Button */}
              <button
                onClick={onStartChat}
                className="w-full py-3 bg-gradient-to-r from-accent-purple to-accent-pink text-white rounded-xl font-medium hover:shadow-lg hover:shadow-accent-purple/25 transform hover:scale-[1.02] transition-all duration-300"
              >
                Start Chat with {typeof companion.name === 'object' && companion.name
                  ? (companion.name.nick_name || `${companion.name.first_name || ''} ${companion.name.last_name || ''}`.trim() || 'Unknown')
                  : String(companion.name || 'Unknown')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanionDetailModal;